import { useCallback, useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Home, FolderKanban, CheckSquare, CalendarDays, ListTodo, LayoutGrid, Network, Gauge, Landmark, Settings, Bell, LogOut, Menu, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAuth } from "@/context/AuthContext";
import { api, ROLE_LABELS } from "@/lib/api";
import { EmailPrefs } from "@/components/EmailPrefs";
import { ChangePassword } from "@/components/ChangePassword";
import { SecurityPanel } from "@/components/SecurityPanel";

const NAV = [
  { to: "/", label: "Home", icon: Home, end: true },
  { to: "/projects", label: "Projects", icon: FolderKanban },
  { to: "/approvals", label: "Approvals", icon: CheckSquare },
  { to: "/my-tasks", label: "My Tasks", icon: ListTodo },
  { to: "/meetings", label: "Meetings", icon: CalendarDays },
  { to: "/portfolio", label: "Portfolio", icon: LayoutGrid, roles: ["pmo", "pgm", "cab", "finance", "sysadmin"] },
  { to: "/director", label: "Director", icon: Gauge, roles: ["pgm", "pmo", "sysadmin"] },
  { to: "/programme", label: "Programme", icon: Network, roles: ["pgm", "pmo"] },
  { to: "/finance", label: "Finance", icon: Landmark, roles: ["finance", "pmo"] },
  { to: "/admin", label: "Admin", icon: Settings, roles: ["sysadmin", "pmo"] },
];

function Bellbox() {
  const [data, setData] = useState({ items: [], unread: 0 });
  const nav = useNavigate();
  const load = useCallback(() => api.get("/notifications").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    window.addEventListener("aspire:notifs", load);
    return () => {
      clearInterval(t);
      window.removeEventListener("aspire:notifs", load);
    };
  }, [load]);
  const open = async (n) => {
    await api.post("/notifications/read", { ids: [n.id] });
    load();
    nav(n.link);
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="notification-bell" className="relative rounded-lg p-2 text-slate-600 hover:bg-slate-100 transition-colors">
          <Bell className="h-5 w-5" />
          {data.unread > 0 && <span data-testid="notification-unread-count" className="absolute -right-0.5 -top-0.5 rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white">{data.unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between border-b px-4 py-2.5">
          <span className="text-sm font-semibold">Notifications</span>
          <button data-testid="notifications-mark-all" className="text-xs text-indigo-600 hover:underline" onClick={() => api.post("/notifications/read", {}).then(load)}>Mark all read</button>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {data.items.length === 0 && <div className="p-6 text-center text-sm text-slate-400">No notifications</div>}
          {data.items.map((n) => (
            <button key={n.id} data-testid={`notification-${n.id}`} onClick={() => open(n)} className={`block w-full border-b px-4 py-2.5 text-left hover:bg-slate-50 ${n.read ? "" : "bg-indigo-50/50"}`}>
              <div className="flex items-start gap-2">
                {!n.read && <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-600" />}
                <div>
                  <div className="text-sm font-medium text-slate-800">{n.title}</div>
                  {n.body && <div className="text-xs text-slate-500">{n.body}</div>}
                  <div className="mt-0.5 text-[10px] uppercase tracking-wider text-slate-400">{n.kind} · {new Date(n.created_at).toLocaleString()}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const [mobile, setMobile] = useState(false);
  const items = NAV.filter((n) => !n.roles || n.roles.includes(user.role));
  const side = (
    <nav className="flex flex-col gap-1 p-3">
      {items.map((n) => (
        <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setMobile(false)} data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
          className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${isActive ? "bg-indigo-600 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"}`}>
          <n.icon className="h-4 w-4" />{n.label}
        </NavLink>
      ))}
    </nav>
  );
  return (
    <div className="min-h-screen">
      <aside className={`fixed inset-y-0 left-0 z-40 w-60 bg-slate-900 transition-transform lg:translate-x-0 ${mobile ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex h-16 items-center gap-2 border-b border-slate-800 px-5">
          <div className="grid h-8 w-8 place-items-center rounded-lg bg-indigo-600 font-display text-lg font-bold text-white">A</div>
          <div>
            <div className="font-display text-lg font-semibold leading-none text-white">Aspire</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Governance</div>
          </div>
          <button className="ml-auto text-slate-400 lg:hidden" onClick={() => setMobile(false)}><X className="h-5 w-5" /></button>
        </div>
        {side}
      </aside>
      {mobile && <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setMobile(false)} />}
      <div className="lg:pl-60">
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-slate-200 bg-white/85 px-4 backdrop-blur-md sm:px-6">
          <button className="rounded p-2 lg:hidden" data-testid="mobile-menu-btn" onClick={() => setMobile(true)}><Menu className="h-5 w-5" /></button>
          <div className="hidden text-xs text-slate-500 lg:block">ASPIRE Project Governance Framework v1.0</div>
          <div className="flex items-center gap-3">
            <EmailPrefs user={user} />
            <ChangePassword />
            {["sysadmin", "finance"].includes(user.role) && !user.email.endsWith("@aspire.demo") && <SecurityPanel />}
            <Bellbox />
            <div className="text-right">
              <div className="text-sm font-medium text-slate-800" data-testid="current-user-name">{user.name}</div>
              <div className="text-xs text-slate-500" data-testid="current-user-role">{ROLE_LABELS[user.role]}</div>
            </div>
            <button data-testid="logout-btn" onClick={logout} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100" title="Sign out"><LogOut className="h-4 w-4" /></button>
          </div>
        </header>
        <main className="mx-auto max-w-[1600px] space-y-6 p-4 sm:p-6 lg:p-8"><Outlet /></main>
      </div>
    </div>
  );
}
